import React, { useState, useEffect, useMemo } from 'react';
import { useApi } from '../../../utils/api';
import { useToast } from '../../../context/ToastContext';
import styles from './MyStats.module.css';

const GROUP_OPTIONS = [
  { value: 'detail', label: 'Detail' },
  { value: 'day',    label: 'Day' },
  { value: 'week',   label: 'Week' },
  { value: 'month',  label: 'Month' },
  { value: 'year',   label: 'Year' },
];

const QUICK_RANGES = [
  { label: 'Today',      from: () => getTodayString(),        to: () => getTodayString() },
  { label: 'This Week',  from: () => getWeekStartString(),    to: () => getTodayString() },
  { label: 'This Month', from: () => getMonthStartString(),   to: () => getTodayString() },
  { label: 'This Year',  from: () => getYearStartString(),    to: () => getTodayString() },
  { label: 'All Time',   from: () => '1970-01-01',            to: () => '2099-12-31' },
];

function MyStats() {
  const api = useApi();
  const { pushToast } = useToast();
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFrom, setDateFrom] = useState(getPastDaysString(30));
  const [dateTo, setDateTo] = useState(getTodayString());
  const [groupBy, setGroupBy] = useState('day');

  useEffect(() => {
    loadStats();
  }, [dateFrom, dateTo]);

  const loadStats = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await api.getMyStats(dateFrom, dateTo);
      setStats(data.stats || []);
    } catch (err) {
      const message = err.message || 'Failed to load stats';
      setError(message);
      pushToast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const applyQuickRange = (range) => {
    setDateFrom(range.from());
    setDateTo(range.to());
  };

  // Derive unique categories (sorted by first appearance)
  const categories = useMemo(() => {
    const seen = new Set();
    const list = [];
    stats.forEach((s) => {
      if (!seen.has(s.category)) {
        seen.add(s.category);
        list.push(s.category);
      }
    });
    return list;
  }, [stats]);

  // Aggregate rows by the selected group
  const groupedRows = useMemo(() => {
    if (groupBy === 'detail') return null;

    const buckets = new Map(); // periodKey => { label, totals: { category => number } }

    stats.forEach((s) => {
      const { key, label } = getPeriodKey(s.stat_date, groupBy);
      if (!buckets.has(key)) {
        buckets.set(key, { label, sortKey: key, totals: {} });
      }
      const bucket = buckets.get(key);
      bucket.totals[s.category] = (bucket.totals[s.category] || 0) + Number(s.stat_value);
    });

    // Sort by period ascending
    const rows = Array.from(buckets.values()).sort((a, b) =>
      a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0
    );

    // Add a totals row
    if (rows.length > 1) {
      const grandTotal = { label: 'TOTAL', sortKey: 'zzz', totals: {}, isTotal: true };
      categories.forEach((cat) => {
        grandTotal.totals[cat] = rows.reduce((sum, r) => sum + (r.totals[cat] || 0), 0);
      });
      rows.push(grandTotal);
    }

    return rows;
  }, [stats, groupBy, categories]);

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>My Stats</h2>

      {error && <div className={styles.alert + ' ' + styles.error} role="alert">{error}</div>}

      {/* Quick range buttons */}
      <div className={styles.quickRanges}>
        {QUICK_RANGES.map((r) => (
          <button
            key={r.label}
            type="button"
            className={styles.quickBtn}
            onClick={() => applyQuickRange(r)}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Date range + Group By */}
      <div className={styles.filterBar}>
        <div className={styles.filterGroup}>
          <label htmlFor="from" className={styles.label}>From</label>
          <input
            id="from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className={styles.input}
          />
        </div>
        <div className={styles.filterGroup}>
          <label htmlFor="to" className={styles.label}>To</label>
          <input
            id="to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className={styles.input}
          />
        </div>
        <div className={styles.filterGroup}>
          <label htmlFor="groupBy" className={styles.label}>Group By</label>
          <select
            id="groupBy"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            className={styles.input}
          >
            {GROUP_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {loading && <p className={styles.loading}>Loading stats...</p>}
      {!loading && stats.length === 0 && (
        <p className={styles.empty}>No stats in this date range</p>
      )}

      {/* Detail view: flat list */}
      {!loading && stats.length > 0 && groupBy === 'detail' && (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <caption className={styles.caption}>Your submitted stats for the selected date range.</caption>
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Category</th>
                <th scope="col">Value</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((stat) => (
                <tr key={stat.id}>
                  <td>{stat.stat_date}</td>
                  <td>{stat.category}</td>
                  <td className={styles.value}>{stat.stat_value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Grouped view: aggregated totals */}
      {!loading && stats.length > 0 && groupBy !== 'detail' && groupedRows && (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <caption className={styles.caption}>
              Totals grouped by {groupBy} for the selected date range.
            </caption>
            <thead>
              <tr>
                <th scope="col">{groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}</th>
                {categories.map((cat) => (
                  <th key={cat} scope="col">{cat}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groupedRows.map((row, idx) => (
                <tr key={idx} className={row.isTotal ? styles.totalRow : ''}>
                  <td className={row.isTotal ? styles.totalLabel : styles.periodLabel}>{row.label}</td>
                  {categories.map((cat) => (
                    <td key={cat} className={row.isTotal ? styles.totalValue : styles.value}>
                      {row.totals[cat] ?? 0}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Return { key, label } for a stat_date string based on groupBy */
function getPeriodKey(dateStr, groupBy) {
  // Parse without timezone shift
  const [year, month, day] = dateStr.split('-').map(Number);

  if (groupBy === 'day') {
    return { key: dateStr, label: dateStr };
  }
  if (groupBy === 'month') {
    const key = `${year}-${String(month).padStart(2, '0')}`;
    const label = new Date(year, month - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
    return { key, label };
  }
  if (groupBy === 'year') {
    return { key: String(year), label: String(year) };
  }
  if (groupBy === 'week') {
    // ISO week number
    const d = new Date(year, month - 1, day);
    const startOfYear = new Date(year, 0, 1);
    const dayOfYear = Math.round((d - startOfYear) / 86400000);
    const weekNum = Math.ceil((dayOfYear + startOfYear.getDay() + 1) / 7);
    const key = `${year}-W${String(weekNum).padStart(2, '0')}`;
    return { key, label: `Week ${weekNum}, ${year}` };
  }
  return { key: dateStr, label: dateStr };
}

function getTodayString() {
  const today = new Date();
  return [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, '0'),
    String(today.getDate()).padStart(2, '0'),
  ].join('-');
}

function getPastDaysString(days) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

function getWeekStartString() {
  const d = new Date();
  const day = d.getDay(); // 0 = Sunday
  d.setDate(d.getDate() - day);
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
}

function getMonthStartString() {
  const d = new Date();
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), '01'].join('-');
}

function getYearStartString() {
  return `${new Date().getFullYear()}-01-01`;
}

export default MyStats;
